/**
 * Light touch-up for voice-dictated text. The STT model already punctuates,
 * capitalizes, and spaces its own output from the speaker's prosody, so this
 * deliberately does NOT insert punctuation. Doing that on top of the model
 * produced doubled marks
 * ("customer, ,") whenever the speaker also said the word, and stray literals
 * ("period" → "periods", "exclamation mark" → "explanation mark") whenever the
 * model misheard the command — so users are told to just speak naturally.
 *
 * What's left is the two things the model can't do from speech alone — explicit
 * line breaks — plus tidily joining the fragment onto the existing draft.
 */

/** Spoken structural commands the model can't infer from prosody. Punctuation
 *  marks are intentionally NOT here: the model already emits those itself, and
 *  re-inserting them doubled up. "new paragraph" is listed before "new line" so
 *  the longer phrase wins. */
function applyStructureCommands(text: string): string {
  return text
    .replace(/\bnew paragraph\b/gi, '\n\n')
    .replace(/\bnew line\b/gi, '\n')
}

/** Collapse only the whitespace our line-break substitutions can introduce —
 *  the model's own punctuation spacing is already correct, so we don't touch it
 *  (that's what used to mangle "p.m." into "p. M"). */
function tidyWhitespace(text: string): string {
  return text
    .replace(/[ \t]*\n[ \t]*/g, '\n') // hug line breaks tight
    .replace(/[ \t]{2,}/g, ' ') // collapse runs of spaces
    .replace(/\s+$/, '') // drop trailing whitespace
}

/** Lift the standalone pronoun "i" to "I" — the one casing fix the model still
 *  misses (it occasionally emits a lowercase "i" mid-utterance). Word-bounded so
 *  it never touches the "i" inside another word. */
function capitalizeStandaloneI(text: string): string {
  return text.replace(/\bi\b/g, 'I')
}

/** Insert the space the model occasionally drops between two sentences it glued
 *  together ("asking.Great" → "asking. Great"). Scoped to a lowercase → .?! →
 *  uppercase boundary, so it never splits an abbreviation ("U.S.", "p.m.") or a
 *  decimal ("3.5") — those aren't lowercase-then-uppercase across the dot. */
function spaceGluedSentences(text: string): string {
  return text.replace(/([a-z])([.?!])([A-Z])/g, '$1$2 $3')
}

/** Capitalize the first alphabetic character, skipping any leading whitespace. */
function capitalizeLeading(text: string): string {
  return text.replace(/^(\s*)([a-z])/, (_m, ws: string, ch: string) =>
    ws.concat(ch.toUpperCase()),
  )
}

/** Whether a dictated fragment appended after `base` begins a fresh sentence —
 *  true when there's nothing before it, or it follows a terminator or newline. */
function startsNewSentence(base: string): boolean {
  return base === '' || /[.!?]["')\]]?\s*$/.test(base) || /\n\s*$/.test(base)
}

/** Append the model's transcript onto the existing draft: expand the line-break
 *  commands, tidy the whitespace they add, capitalize the fragment only when it
 *  opens a new sentence (start of draft, or after a terminator/newline), and
 *  insert a separating space unless the draft already ends in whitespace (or the
 *  fragment starts with its own line break). */
export function joinDictation(base: string, rawSession: string): string {
  const session = spaceGluedSentences(
    capitalizeStandaloneI(tidyWhitespace(applyStructureCommands(rawSession))),
  )
  if (!session) return base
  const body = startsNewSentence(base) ? capitalizeLeading(session) : session
  const joined =
    !base || base.endsWith(' ') || base.endsWith('\n') || body.startsWith('\n')
  return joined ? base + body : `${base} ${body}`
}
